App.namespace.wrap('es6.shared.apple_login_manager', function (options = {}) {
  const {
    buttonId,
    appleUrl,
    appleClientId,
    appleRedirectUri,
    scope = 'name email',
    csrfToken,
    callback,
    onError,
    autoLoginOnClick,
    trackingParams = {},
  } = options

  const APPLE_API_URL = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
  const GENERIC_ERROR_MESSAGE = 'There was an error trying to login with Apple, please try again'
  const UNAVAILABLE_MESSAGE = 'Apple Authentication is currently unavailable. Please try again later.'
  const POPUP_BLOCKED_MESSAGE =
    'Your browser blocked the Apple sign-in popup. Please allow popups for this site and try again.'

  // Prevents overlapping sign-in flows (double-click / re-trigger), which Apple rejects
  // with `user_trigger_new_signin_flow`.
  let signInInProgress = false;

  const getButton = () => (buttonId ? document.getElementById(buttonId) : null);

  const isAppleApiReady = () => !!(window.AppleID && window.AppleID.auth);

  const releaseLock = () => {
    signInInProgress = false;
    const button = getButton();
    if (button) {
      button.disabled = false;
    }
  };

  const reportToUser = (message) => {
    if (typeof onError === 'function') {
      onError(message);
      return;
    }

    App.es6.shared.social_login_error.show(message);
  };

  const sendAjaxRequest = (authorization, user) => {
    $.ajax({
      method: "POST",
      url: appleUrl,
      data: {
        idtoken: authorization.id_token,
        code: authorization.code,
        firstName: user && user.name ? user.name.firstName : '',
        lastName: user && user.name ? user.name.lastName : '',
        _token: csrfToken,
        trackingParams: typeof trackingParams === 'function' ? trackingParams() : trackingParams,
      },
      contentType: 'application/x-www-form-urlencoded',
      success: (response) => {
        // finally guarantees the lock is released even if the caller's callback throws
        // (a throwing callback would stop jQuery from reaching an `always`/`complete` handler).
        try {
          if (typeof callback === 'function') {
            callback(response);
          } else if (typeof window[callback] === 'function') {
            window[callback](response);
          }
        } finally {
          releaseLock();
        }
      },
      error: (jqXHR) => {
        try {
          const refusalMessage = App.es6.shared.social_login_error.getRefusalMessage(jqXHR);

          if (!refusalMessage) {
            Rollbar.critical('Failed apple login attempt');
          }

          reportToUser(refusalMessage || GENERIC_ERROR_MESSAGE);
        } finally {
          releaseLock();
        }
      }
    });
  }

  const api = {
    async triggerModal() {
      if (signInInProgress) {
        return;
      }

      // The button becomes clickable before the async-loaded Apple SDK is ready
      // (or when it failed to load). Guard here so we never dereference
      // `window.AppleID.auth` on an undefined SDK, which throws a TypeError that
      // surfaces as a misleading "Apple sign-in error" in Rollbar.
      if (!isAppleApiReady()) {
        Rollbar.warning('Apple sign-in clicked before SDK was ready');
        return;
      }

      signInInProgress = true;
      const button = getButton();
      if (button) {
        button.disabled = true;
      }

      try {
        const data = await window.AppleID.auth.signIn();
        // Lock is released by the ajax `complete` handler once the POST settles.
        sendAjaxRequest(data.authorization, data.user);
      } catch (error) {
        releaseLock();

        const errorCode = error && error.error;

        // Closing the popup or double-triggering the flow are user-initiated and expected.
        if (errorCode === 'popup_closed_by_user' || errorCode === 'user_trigger_new_signin_flow') {
          return;
        }

        // A blocked popup is a browser/user setting, not an app fault, so warn instead of
        // error to keep Rollbar quiet – but still surface a message so the user can act.
        if (errorCode === 'popup_blocked_by_browser') {
          Rollbar.warning('Apple sign-in popup blocked by browser');
          reportToUser(POPUP_BLOCKED_MESSAGE);
          return;
        }

        Rollbar.error('Apple sign-in error', error);
        reportToUser(UNAVAILABLE_MESSAGE);
      }
    },
  }

  const bindApple = () => {
    window.AppleID.auth.init({
      clientId: appleClientId,
      scope,
      redirectURI: appleRedirectUri,
      usePopup: true,
    });

    if (autoLoginOnClick) {
      document.getElementById(buttonId).addEventListener('click', () => {
        api.triggerModal();
      });
    }
  };

  const waitForAppleAPI = (initCallback) => {
    App.es6.shared.waitFor(() => {
      return isAppleApiReady();
    }, error => {
      if (error) {
        window._APPLE_API_ERROR = 'Couldn\'t load apple api.';
        initCallback();
        return;
      }
      bindApple();
      initCallback();
    }, {attempts: 20});
  };

  const noop = () => null;
  const init = (initCallback = noop) => {
    $.getScript(APPLE_API_URL)
      .done(() => waitForAppleAPI(initCallback))
      .fail(() => {
        window._APPLE_API_ERROR = 'Couldn\'t load apple api.';
        initCallback();
      });

    return api;
  };

  return {
    init
  }
});
